import React from 'react';
import { setHours, setMinutes } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAbsoluteTimeFormat } from '@/hooks/useAbsoluteTimeFormat';
import { keepOffset } from '@/lib/date-utils';
import { zoneName } from '@/lib/displayTimeZone';
import { cn } from '@/lib/utils';

export interface TimePickerProps {
  date: Date;
  onChange: (date: Date) => void;
  className?: string;
  /** Zone named next to the label: `date`'s own (pass a `TZDate` in it). */
  timeZone?: string;
}

const pad = (n: number): string => n.toString().padStart(2, '0');

/**
 * TimePicker - hours and minutes in the user's clock (Settings → Preferences
 * → Timestamp Format): hours 0-23, or 1-12 next to an AM/PM toggle, so the
 * editor reads like the timestamp it edits.
 * Edits keep `date`'s type, so a `TZDate` is edited in its own zone.
 */
export const TimePicker: React.FC<TimePickerProps> = ({
  date,
  onChange,
  className,
  timeZone,
}) => {
  const twelveHour = useAbsoluteTimeFormat().clock === '12h';
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const pm = hours >= 12;
  // The hour as the field shows it: 0-23, or 1-12 beside AM/PM.
  const shownHours = twelveHour ? ((hours + 11) % 12) + 1 : hours;
  const [minHour, maxHour] = twelveHour ? [1, 12] : [0, 23];

  // Local state for input values to allow free typing
  const [hoursInput, setHoursInput] = React.useState(pad(shownHours));
  const [minutesInput, setMinutesInput] = React.useState(pad(minutes));

  // Update local state when external date changes
  React.useEffect(() => {
    setHoursInput(pad(shownHours));
  }, [shownHours]);

  React.useEffect(() => {
    setMinutesInput(pad(minutes));
  }, [minutes]);

  // Only typing is an edit: a field focused and left as it was doesn't reach
  // onChange, but retyping the time it shows does (the user confirmed it).
  const hoursTyped = React.useRef(false);
  const minutesTyped = React.useRef(false);

  const updateHours = (value: string): void => {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= minHour && num <= maxHour) {
      onChange(
        keepOffset(
          date,
          setHours(date, twelveHour ? (num % 12) + (pm ? 12 : 0) : num)
        )
      );
    }
  };

  const updateMinutes = (value: string): void => {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= 0 && num <= 59) {
      onChange(keepOffset(date, setMinutes(date, num)));
    }
  };

  const setPeriod = (nextPm: boolean): void => {
    if (nextPm !== pm)
      onChange(keepOffset(date, setHours(date, (hours + 12) % 24)));
  };

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    // Allow empty or numeric input (max 2 digits)
    if (value === '' || /^\d{0,2}$/.test(value)) {
      hoursTyped.current = true;
      setHoursInput(value);
    }
  };

  const handleHoursBlur = (): void => {
    if (!hoursTyped.current) return;
    hoursTyped.current = false;
    // An empty field is the period's first hour: 00, or 12 beside AM/PM.
    const num =
      hoursInput === '' ? (twelveHour ? 12 : 0) : parseInt(hoursInput, 10);
    if (isNaN(num)) {
      setHoursInput(pad(shownHours));
      return;
    }
    // Beside AM/PM, 0 and 13-23 are 24-hour times (23 is 11 PM), not clamped
    // into the current period.
    if (twelveHour && (num === 0 || (num > 12 && num < 24))) {
      setHoursInput(pad(((num + 11) % 12) + 1));
      onChange(keepOffset(date, setHours(date, num)));
      return;
    }
    // Clamp to valid range
    const clamped = Math.max(minHour, Math.min(maxHour, num));
    setHoursInput(pad(clamped));
    updateHours(clamped.toString());
  };

  const handleMinutesChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = e.target.value;
    // Allow empty or numeric input (max 2 digits)
    if (value === '' || /^\d{0,2}$/.test(value)) {
      minutesTyped.current = true;
      setMinutesInput(value);
    }
  };

  const handleMinutesBlur = (): void => {
    if (!minutesTyped.current) return;
    minutesTyped.current = false;
    const num = minutesInput === '' ? 0 : parseInt(minutesInput, 10);
    if (isNaN(num)) {
      setMinutesInput(pad(minutes));
      return;
    }
    // Clamp to valid range
    const clamped = Math.max(0, Math.min(59, num));
    setMinutesInput(pad(clamped));
    updateMinutes(clamped.toString());
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.currentTarget.blur(); // Trigger blur to validate and format
    }
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <label className="text-xs font-medium text-muted-foreground">
        Time{timeZone && ` (${zoneName(timeZone)})`}
      </label>
      <div className="flex items-center gap-2 justify-center">
        <Input
          type="text"
          inputMode="numeric"
          value={hoursInput}
          onChange={handleHoursChange}
          onBlur={handleHoursBlur}
          onKeyDown={handleKeyDown}
          className="w-16 text-center"
          placeholder="HH"
        />
        <span className="text-muted-foreground font-medium">:</span>
        <Input
          type="text"
          inputMode="numeric"
          value={minutesInput}
          onChange={handleMinutesChange}
          onBlur={handleMinutesBlur}
          onKeyDown={handleKeyDown}
          className="w-16 text-center"
          placeholder="MM"
        />
        {twelveHour && (
          <div role="group" aria-label="AM or PM" className="flex">
            {(['AM', 'PM'] as const).map(period => {
              const active = (period === 'PM') === pm;
              return (
                <Button
                  key={period}
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-pressed={active}
                  onClick={() => setPeriod(period === 'PM')}
                  className={cn(
                    'first:rounded-r-none last:rounded-l-none last:border-l-0',
                    active &&
                      'bg-accent text-accent-foreground border-accent-border hover:bg-accent'
                  )}
                >
                  {period}
                </Button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
